import React, { useEffect, useState } from 'react';
import { FiMail, FiLock, FiChevronRight, FiUser, FiPhone, FiEye, FiEyeOff } from 'react-icons/fi';
import { updateFavicon, updatePageTitle } from '../utils/faviconUtils';
import api from '../api';

const defaultFavicon = '/T.png';

const DefaultTemplate = ({
  subdomain,
  handleSignIn,
  email,
  setEmail,
  password,
  setPassword,
  loginError,
  buttonLoading
}) => {
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactModal, setContactModal] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Fetch organization data
  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/basic-info/${subdomain || 'default'}`);
        const orgInfo = response.data;
        
        setOrgData(orgInfo);
        
        const faviconUrl = orgInfo.logo || defaultFavicon;
        const orgName = orgInfo.name || 'Truyon';
        
        updateFavicon(faviconUrl);
        updatePageTitle(orgName);
        
        try {
          localStorage.setItem('tenantFavicon', faviconUrl);
          localStorage.setItem('tenantTitle', orgName);
        } catch (e) {
          // ignore storage errors
        }
        
        console.log('🎨 Applied organization branding:', orgInfo);
      } catch (error) {
        console.error('Failed to fetch organization data:', error);
        updateFavicon(defaultFavicon);
        updatePageTitle('Truyon');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationData();
  }, [subdomain]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', contactForm);
    setContactModal(false);
    setContactForm({ name: '', email: '', phone: '', message: '' });
    alert('Your message has been sent to the administrator. We will get back to you soon.');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email?.trim()) {
      setForgotPasswordMessage('Please enter your email address first.');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const response = await api.post('/api/new-auth/forgot-password', {
        email: email.trim().toLowerCase()
      });

      if (response.data?.resetUrl) {
        setForgotPasswordMessage('Reset link generated. Check the server response during development to copy the link.');
      } else {
        setForgotPasswordMessage('If the email exists, a password reset link has been sent.');
      }
    } catch (error) {
      setForgotPasswordMessage(error.response?.data?.message || 'Unable to request password reset. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // ✅ Wrap handleSignIn to store subscription data
  const handleSignInWithSubscription = async (e) => {
    e.preventDefault();
    
    try {
      // Call the original handleSignIn
      await handleSignIn(e);
      
      // The subscription data is stored in the parent Dashboard component
      // which calls this function. The Dashboard already stores subscription.
      
    } catch (error) {
      // Error is handled by parent
      console.error('Login error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization information...</p>
        </div>
      </div>
    );
  }

  const organizationName = orgData?.name || 'Truyon';
  const organizationLogo = orgData?.logo || defaultFavicon;
  const primaryColor = orgData?.primaryColor || 'blue';

  const colorClasses = {
    blue: {
      gradient: 'from-blue-600 to-blue-800',
      gradientHover: 'from-blue-700 to-blue-900',
      focus: 'focus:ring-blue-500 focus:border-blue-500',
      text: 'text-blue-600',
      textHover: 'hover:text-blue-500'
    },
    green: {
      gradient: 'from-green-600 to-green-800',
      gradientHover: 'from-green-700 to-green-900',
      focus: 'focus:ring-green-500 focus:border-green-500',
      text: 'text-green-600',
      textHover: 'hover:text-green-500'
    },
    purple: {
      gradient: 'from-purple-600 to-purple-800',
      gradientHover: 'from-purple-700 to-purple-900',
      focus: 'focus:ring-purple-500 focus:border-purple-500',
      text: 'text-purple-600',
      textHover: 'hover:text-purple-500'
    },
    indigo: {
      gradient: 'from-indigo-600 to-indigo-800',
      gradientHover: 'from-indigo-700 to-indigo-900',
      focus: 'focus:ring-indigo-500 focus:border-indigo-500',
      text: 'text-indigo-600',
      textHover: 'hover:text-indigo-500'
    }
  };

  const colors = colorClasses[primaryColor] || colorClasses.blue;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className={`bg-gradient-to-r ${colors.gradient} py-6 shadow-lg`}>
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img 
                src={organizationLogo} 
                alt={`${organizationName} logo`}
                className="h-10 w-10 rounded-lg object-contain bg-white p-1"
                onError={(e) => {
                  e.target.src = defaultFavicon;
                }}
              />
              <h1 className="text-2xl font-bold text-white">
                {organizationName}
              </h1>
            </div>
            <div className="text-sm text-blue-100">
              Secure Login Portal
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-[1.01] transition-all duration-300">
          {/* Decorative Header */}
          <div className={`bg-gradient-to-r ${colors.gradient} py-6 px-8 relative overflow-hidden`}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-bold text-white mb-2">Welcome Back</h2>
              <p className="text-blue-100 text-sm">Sign in to your {organizationName} account</p>
            </div>
          </div>
          
          {/* Form Container */}
          <div className="p-8">
            <form onSubmit={handleSignInWithSubscription} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Email Address</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-600">
                    <FiMail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 ${colors.focus} focus:border-transparent transition-all group-hover:border-gray-400`}
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-600">
                    <FiLock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`block w-full pl-10 pr-11 py-3 border border-gray-300 rounded-lg focus:ring-2 ${colors.focus} focus:border-transparent transition-all group-hover:border-gray-400`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {loginError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start animate-shake">
                  <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-red-600">{loginError}</span>
                  </div>
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>

                <button 
                  type="button"
                  onClick={() => setForgotPasswordModal(true)}
                  className={`text-sm font-medium ${colors.text} ${colors.textHover} underline bg-transparent border-none cursor-pointer`}
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={buttonLoading}
                aria-busy={buttonLoading}
                aria-label={buttonLoading ? "Signing In..." : "Sign In"}
                className={`w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                  buttonLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {buttonLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    Signing In...
                  </>
                ) : (
                  <>
                    Sign In
                    <FiChevronRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            {/* Additional Links */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <span className="text-gray-600 text-sm">Need an account? </span>
              <button 
                type="button"
                onClick={() => setContactModal(true)}
                className={`font-medium ${colors.text} ${colors.textHover} underline bg-transparent border-none cursor-pointer text-sm`}
              >
                Contact administrator
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white py-6 border-t border-gray-200">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} {organizationName}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Contact Administrator Modal */}
      {contactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-scaleIn">
            <div className={`bg-gradient-to-r ${colors.gradient} py-4 px-6 rounded-t-2xl`}>
              <h3 className="text-xl font-bold text-white">Contact Administrator</h3>
            </div>
            <form onSubmit={handleContactSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  required
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Please provide details about why you need an account..."
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setContactModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex-1 py-2 px-4 bg-gradient-to-r ${colors.gradient} text-white rounded-lg hover:${colors.gradientHover} transition-colors`}
                >
                  Send Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {forgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-scaleIn">
            <div className={`bg-gradient-to-r ${colors.gradient} py-4 px-6 rounded-t-2xl`}>
              <h3 className="text-xl font-bold text-white">Reset Password</h3>
            </div>
            <form onSubmit={handleForgotPassword} className="p-6 space-y-4">
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              {forgotPasswordMessage && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {forgotPasswordMessage}
                </div>
              )}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordModal(false);
                    setForgotPasswordMessage('');
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className={`flex-1 py-2 px-4 bg-gradient-to-r ${colors.gradient} text-white rounded-lg hover:${colors.gradientHover} transition-colors`}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default DefaultTemplate;